import React, { useState, useEffect, useLayoutEffect } from "react";
import LocationPickerModal, { LocationData } from "./LocationPickerModal";
import { useProfile } from "../context/ProfileContext";
import { AddressBookData } from "../services/index";
import { GOOGLE_MAPS_API_KEY } from "../utils/constants";
import { getAddressDeliveryInstructions } from "../utils/helpers";

interface AddAddressModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (address: AddressData) => void;
  googleMapsApiKey?: string;
  initialLocationData?: {
    address: string;
    pincode: string;
    city: string;
    state: string;
    coordinates: {
      lat: number;
      lng: number;
    };
    fullAddress: string;
  } | null;
  existingAddress?: AddressBookData | null;
}

export interface AddressData {
  fullName: string;
  phone: string;
  locationType: "house" | "office" | "other";
  buildingFloor: string;
  street: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  pincode: string;
  fullAddress: string;
  saveAddressAs: string;
  deliveryInstructions: string[];
  latitude: number;
  longitude: number;
  geoLocation: {
    type: "Point";
    coordinates: [number, number]; // [longitude, latitude]
  };
}

const AddAddressModal: React.FC<AddAddressModalProps> = ({
  isOpen,
  onClose,
  onSave,
  googleMapsApiKey = GOOGLE_MAPS_API_KEY,
  initialLocationData,
  existingAddress,
}) => {
  const { profile } = useProfile();
  const [isLocationPickerOpen, setIsLocationPickerOpen] = useState(false);
  const [useAccountDetails, setUseAccountDetails] = useState(false);
  const [newInstruction, setNewInstruction] = useState("");

  console.log("existingAddress_inside_modal", existingAddress);
  
  // Helper function to get initial empty form data
  const getInitialFormData = (): AddressData => ({
    fullName: "",
    phone: "",
    locationType: "house",
    buildingFloor: "",
    street: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    pincode: "",
    fullAddress: "",
    saveAddressAs: "",
    deliveryInstructions: [],
    latitude: 0,
    longitude: 0,
    geoLocation: {
      type: "Point",
      coordinates: [0, 0],
    },
  });
  
  // Initialize with empty form data
  const [formData, setFormData] = useState<AddressData>(getInitialFormData());
  const [errors, setErrors] = useState<Partial<Record<keyof AddressData, string>>>({});

  // Auto-select checkbox when editing existing address
  useEffect(() => {
    if (isOpen && existingAddress) {
      setUseAccountDetails(true);
    } else if (isOpen && !existingAddress) {
      setUseAccountDetails(false);
    }
  }, [isOpen, existingAddress]);

  // Update form data when modal opens or initialLocationData/existingAddress changes
  useLayoutEffect(() => {
    if (isOpen && existingAddress && initialLocationData) {
      // Editing with new location - merge existing user data with new location
      console.log("AddAddressModal - Editing address with new location");
      setFormData({
        fullName: existingAddress.fullName || "",
        phone: existingAddress.phone || "",
        locationType: "house",
        buildingFloor: "",
        street: "",
        addressLine1: initialLocationData.address || "",
        addressLine2: existingAddress.addressLine2 || "",
        city: initialLocationData.city || "",
        state: initialLocationData.state || "",
        pincode: initialLocationData.pincode || "",
        fullAddress: initialLocationData.fullAddress || "",
        saveAddressAs: "",
        deliveryInstructions:
          getAddressDeliveryInstructions(existingAddress._id).length > 0
            ? getAddressDeliveryInstructions(existingAddress._id)
            : Array.isArray(existingAddress.deliveryInstructions)
            ? existingAddress.deliveryInstructions
            : String(existingAddress.deliveryInstructions || "")
                .split(". ")
                .map((item) => item.trim())
                .filter(Boolean),
        latitude: initialLocationData.coordinates?.lat || 0,
        longitude: initialLocationData.coordinates?.lng || 0,
        geoLocation: {
          type: "Point",
          coordinates: [
            initialLocationData.coordinates.lng,
            initialLocationData.coordinates.lat,
          ],
        },
      });
    } else if (isOpen && existingAddress) {
      // Editing existing address without new location - use existing location
      const lat = existingAddress.geoLocation?.coordinates?.[1] || 0;
      const lng = existingAddress.geoLocation?.coordinates?.[0] || 0;

      // Construct fullAddress from individual fields
      const addressParts = [];
      if (existingAddress.addressLine1) addressParts.push(existingAddress.addressLine1);
      if (existingAddress.addressLine2) addressParts.push(existingAddress.addressLine2);
      if (existingAddress.city) addressParts.push(existingAddress.city);
      if (existingAddress.state) addressParts.push(existingAddress.state);
      if (existingAddress.pincode) addressParts.push(existingAddress.pincode);
      const fullAddressValue = addressParts.length > 0 ? addressParts.join(', ') + ', India' : '';

      setFormData({
        fullName: existingAddress.fullName || "",
        phone: existingAddress.phone || "",
        locationType: "house",
        buildingFloor: "",
        street: "",
        addressLine1: existingAddress.addressLine1 || "",
        addressLine2: existingAddress.addressLine2 || "",
        city: existingAddress.city || "",
        state: existingAddress.state || "",
        pincode: existingAddress.pincode || "",
        fullAddress: fullAddressValue,
        saveAddressAs: "",
        deliveryInstructions:
          getAddressDeliveryInstructions(existingAddress._id).length > 0
            ? getAddressDeliveryInstructions(existingAddress._id)
            : Array.isArray(existingAddress.deliveryInstructions)
            ? existingAddress.deliveryInstructions
            : String(existingAddress.deliveryInstructions || "")
                .split(". ")
                .map((item) => item.trim())
                .filter(Boolean),
        latitude: lat,
        longitude: lng,
        geoLocation: {
          type: "Point",
          coordinates: [lng, lat],
        },
      });
    } else if (isOpen && initialLocationData) {
      setFormData({
        fullName: "",
        phone: "",
        locationType: "house",
        buildingFloor: "",
        street: "",
        addressLine1: initialLocationData.address || "",
        addressLine2: "",
        city: initialLocationData.city || "",
        state: initialLocationData.state || "",
        pincode: initialLocationData.pincode || "",
        fullAddress: initialLocationData.fullAddress || "",
        saveAddressAs: "",
        deliveryInstructions: [],
        latitude: initialLocationData.coordinates?.lat || 0,
        longitude: initialLocationData.coordinates?.lng || 0,
        geoLocation: {
          type: "Point",
          coordinates: [
            initialLocationData.coordinates.lng,
            initialLocationData.coordinates.lat,
          ],
        },
      });
    } else if (isOpen && !initialLocationData && !existingAddress) {
      console.log("AddAddressModal - No initial location data, keeping empty form");
    }
  }, [isOpen, initialLocationData, existingAddress]);

  // Handle use account details checkbox
  useEffect(() => {
    if (useAccountDetails && profile) {
      setFormData((prev) => ({
        ...prev,
        fullName: `${profile.firstName || ""} ${profile.lastName || ""}`.trim(),
        phone: profile.phoneNumber || "",
      }));
    } else if (!useAccountDetails && isOpen) {
      // Clear only if not using account details
      setFormData((prev) => ({
        ...prev,
        fullName: "",
        phone: "",
      }));
    }
  }, [useAccountDetails, profile, isOpen]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name as keyof AddressData]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleLocationSelect = (location: LocationData) => {
    console.log("handleLocationSelect called with:", location);
    console.log("fullAddress from location:", location.fullAddress);
    
    setFormData((prev) => ({
      ...prev,
      addressLine1: location.address,
      city: location.city,
      state: location.state,
      pincode: location.pincode,
      fullAddress: location.fullAddress,
      geoLocation: {
        type: "Point",
        coordinates: [location.coordinates.lng, location.coordinates.lat],
      },
    }));
    setIsLocationPickerOpen(false);
  };

  const handleOpenLocationPicker = () => {
    setIsLocationPickerOpen(true);
  };

  const handleAddInstruction = () => {
    if (newInstruction.trim()) {
      setFormData((prev) => ({
        ...prev,
        deliveryInstructions: [...prev.deliveryInstructions, newInstruction.trim()],
      }));
      setNewInstruction("");
    }
  };

  const handleRemoveInstruction = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      deliveryInstructions: prev.deliveryInstructions.filter((_, i) => i !== index),
    }));
  };

  const validateForm = () => {
    const newErrors: Partial<Record<keyof AddressData, string>> = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = "Full name is required";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^[+]?[0-9]{10,13}$/.test(formData.phone.replace(/\s/g, ""))) {
      newErrors.phone = "Please enter a valid phone number";
    }

    if (!formData.buildingFloor.trim()) {
      newErrors.buildingFloor = "Building/Floor is required";
    }

    if (!formData.city.trim()) {
      newErrors.city = "City is required";
    }

    if (!formData.state.trim()) {
      newErrors.state = "State is required";
    }

    if (!formData.pincode.trim()) {
      newErrors.pincode = "Pincode is required";
    } else if (!/^[0-9]{6}$/.test(formData.pincode)) {
      newErrors.pincode = "Please enter a valid 6-digit pincode";
    }

    if (
      formData.geoLocation.coordinates[0] === 0 &&
      formData.geoLocation.coordinates[1] === 0
    ) {
      newErrors.geoLocation = "Please select your location";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSave(formData);
      // Reset form
      setFormData(getInitialFormData());
      setErrors({});
      setUseAccountDetails(false);
      setNewInstruction("");
      onClose();
    }
  };

  const handleClose = () => {
    setFormData(getInitialFormData());
    setErrors({});
    setUseAccountDetails(false);
    setNewInstruction("");
    onClose();
  };
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1001] flex items-start md:items-center justify-center bg-black/50 p-0 md:p-4">
      <div className="relative w-full max-w-[500px] bg-white dark:bg-gray-800 md:rounded-[20px] shadow-xl max-h-[94vh] overflow-y-auto">
        {/* Header with Address */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b dark:border-gray-700 px-6 py-4 rounded-t-[20px] z-10">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <button
                onClick={handleClose}
                className="mb-2"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              {(() => {
                // Show address from initialLocationData if available
                if (initialLocationData) {
                  return (
                    <p className="text-sm text-gray-700 dark:text-gray-300 font-medium truncate">
                      {initialLocationData.pincode} | {initialLocationData.fullAddress}
                    </p>
                  );
                }
                
                // Show address from existingAddress if available (for editing saved addresses)
                if (existingAddress && existingAddress.pincode) {
                  const addressParts = [];
                  if (existingAddress.addressLine1) addressParts.push(existingAddress.addressLine1);
                  if (existingAddress.addressLine2) addressParts.push(existingAddress.addressLine2);
                  if (existingAddress.city) addressParts.push(existingAddress.city);
                  if (existingAddress.state) addressParts.push(existingAddress.state);
                  if (existingAddress.pincode) addressParts.push(existingAddress.pincode);
                  const fullAddress = addressParts.length > 0 ? addressParts.join(', ') : '';
                  
                  return (
                    <p className="text-sm text-gray-700 dark:text-gray-300 font-medium truncate">
                      {existingAddress.pincode} | {fullAddress}
                    </p>
                  );
                }
                
                // Show address from formData if available (for other cases)
                if (formData.pincode && formData.fullAddress) {
                  return (
                    <p className="text-sm text-gray-700 dark:text-gray-300 font-medium truncate">
                      {formData.pincode} | {formData.fullAddress}
                    </p>
                  );
                }
                
                return null;
              })()}
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="px-6 py-5 space-y-5">
          {/* Receiver Details Section */}
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              Receiver Details
            </h2>

            {/* Use Account Details Checkbox */}
            <div
              onClick={() => setUseAccountDetails(!useAccountDetails)}
              className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-700 rounded-xl cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors mb-4"
            >
              <div className="flex-shrink-0 mt-0.5">
                <div
                  className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                    useAccountDetails
                      ? "bg-purple-600 border-purple-600"
                      : "border-gray-300 dark:border-gray-500"
                  }`}
                >
                  {useAccountDetails && (
                    <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                      <path d="M1 5L4.5 8.5L11 1.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </div>
              </div>
              <div className="flex-1">
                <p className="text-base font-medium text-gray-900 dark:text-white">
                  Use my account details
                </p>
                {profile && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                    {profile.firstName} {profile.lastName}, {profile.phoneNumber}
                  </p>
                )}
              </div>
            </div>

            {/* Full Name */}
            <div className="mb-4">
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleInputChange}
                placeholder="Full Name *"
                className={`w-full px-4 py-3.5 rounded-xl border ${
                  errors.fullName
                    ? "border-red-500"
                    : "border-gray-200 dark:border-gray-600"
                } bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500`}
              />
              {errors.fullName && (
                <p className="text-red-500 text-xs mt-1">{errors.fullName}</p>
              )}
            </div>

            {/* Phone Number */}
            <div>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="Phone Number *"
                className={`w-full px-4 py-3.5 rounded-xl border ${
                  errors.phone
                    ? "border-red-500"
                    : "border-gray-200 dark:border-gray-600"
                } bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500`}
              />
              {errors.phone && (
                <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
              )}
            </div>
          </div>

          {/* Location Details Section */}
          <div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              Location Details
            </h3>

            {/* Location Type Tabs */}
            <div className="flex gap-2 mb-4 p-1.5 bg-gray-100 dark:bg-gray-700 rounded-xl">
              <button
                type="button"
                onClick={() => setFormData((prev) => ({ ...prev, locationType: "house" }))}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors ${
                  formData.locationType === "house"
                    ? "bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm"
                    : "text-gray-600 dark:text-gray-400"
                }`}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M9 22V12H15V22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                House
              </button>
              <button
                type="button"
                onClick={() => setFormData((prev) => ({ ...prev, locationType: "office" }))}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors ${
                  formData.locationType === "office"
                    ? "bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm"
                    : "text-gray-600 dark:text-gray-400"
                }`}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M3 21H21M3 7V21M7 7V21M11 7V21M15 7V21M19 7V21M7 3H17L19 7H5L7 3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Office
              </button>
              <button
                type="button"
                onClick={() => setFormData((prev) => ({ ...prev, locationType: "other" }))}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors ${
                  formData.locationType === "other"
                    ? "bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm"
                    : "text-gray-600 dark:text-gray-400"
                }`}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Other
              </button>
            </div>

            {/* Building / Floor */}
            <div className="mb-4">
              <input
                type="text"
                name="buildingFloor"
                value={formData.buildingFloor}
                onChange={handleInputChange}
                placeholder="Building / Floor *"
                className={`w-full px-4 py-3.5 rounded-xl border ${
                  errors.buildingFloor
                    ? "border-red-500"
                    : "border-gray-200 dark:border-gray-600"
                } bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500`}
              />
              {errors.buildingFloor && (
                <p className="text-red-500 text-xs mt-1">{errors.buildingFloor}</p>
              )}
            </div>

            {/* Street */}
            <div className="mb-4">
              <input
                type="text"
                name="street"
                value={formData.street}
                onChange={handleInputChange}
                placeholder="Street (Recommended)"
                className="w-full px-4 py-3.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>

            {/* Area with Change Button */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Area
              </label>
              <div className="flex items-start gap-3">
                <div className="flex-1 px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-300 text-sm min-h-[80px]">
                  {(() => {
                    console.log("Area display - formData:", {
                      fullAddress: formData.fullAddress,
                      coordinates: formData.geoLocation.coordinates,
                      city: formData.city,
                      state: formData.state,
                      pincode: formData.pincode,
                      addressLine1: formData.addressLine1
                    });
                    
                    // Prioritize fullAddress from LocationPickerModal
                    if (formData.fullAddress && formData.fullAddress.trim()) {
                      console.log("Displaying fullAddress from modal:", formData.fullAddress);
                      return <p className="break-words">{formData.fullAddress}</p>;
                    }
                    
                    // Check if we have coordinates (location selected but no fullAddress)
                    const hasCoordinates = formData.geoLocation.coordinates[0] !== 0 && 
                                          formData.geoLocation.coordinates[1] !== 0;
                    
                    // Construct address from individual fields if available
                    if (hasCoordinates && (formData.city || formData.state || formData.pincode)) {
                      const parts = [];
                      if (formData.addressLine1) parts.push(formData.addressLine1);
                      if (formData.city) parts.push(formData.city);
                      if (formData.state) parts.push(formData.state);
                      if (formData.pincode) parts.push(formData.pincode);
                      parts.push('India');
                      const constructedAddress = parts.join(', ');
                      console.log("Displaying constructed address:", constructedAddress);
                      return <p className="break-words">{constructedAddress}</p>;
                    }
                    
                    // No location selected
                    return <p className="text-gray-400">No location selected</p>;
                  })()}
                </div>
                <button
                  type="button"
                  onClick={handleOpenLocationPicker}
                  className="flex flex-col items-center justify-center px-4 py-3 rounded-xl bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="mb-1">
                    <path d="M12 13.43C13.7231 13.43 15.12 12.0331 15.12 10.31C15.12 8.58687 13.7231 7.19 12 7.19C10.2769 7.19 8.88 8.58687 8.88 10.31C8.88 12.0331 10.2769 13.43 12 13.43Z" stroke="#7402A7" strokeWidth="1.5"/>
                    <path d="M3.62001 8.49C5.59001 -0.169998 18.42 -0.159997 20.38 8.5C21.53 13.58 18.37 17.88 15.6 20.54C13.59 22.48 10.41 22.48 8.39001 20.54C5.63001 17.88 2.47001 13.57 3.62001 8.49Z" stroke="#7402A7" strokeWidth="1.5"/>
                  </svg>
                  <span className="text-xs font-semibold text-purple-600 dark:text-purple-400">Change</span>
                </button>
              </div>
            </div>

            {/* Save Address As */}
            <div className="mb-4">
              <input
                type="text"
                name="saveAddressAs"
                value={formData.saveAddressAs}
                onChange={handleInputChange}
                placeholder="Save address as (e.g., Home, Work)"
                className="w-full px-4 py-3.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>

            {/* Delivery Instructions */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Delivery instruction
              </label>
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={newInstruction}
                  onChange={(e) => setNewInstruction(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddInstruction();
                    }
                  }}
                  placeholder="Add delivery instructions"
                  className="flex-1 px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                <button
                  type="button"
                  onClick={handleAddInstruction}
                  className="px-6 py-3 rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-medium transition-colors"
                >
                  Add
                </button>
              </div>
              {/* Display added instructions */}
              {formData.deliveryInstructions.length > 0 && (
                <div className="space-y-2">
                  {formData.deliveryInstructions.map((instruction, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between px-4 py-2 bg-purple-50 dark:bg-purple-900/20 rounded-lg"
                    >
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {instruction}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRemoveInstruction(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white dark:bg-gray-800 border-t dark:border-gray-700 px-6 py-4 rounded-b-[20px]">
          <button
            onClick={handleSubmit}
            className="w-full px-6 py-3.5 rounded-full bg-[#7402A7] text-white font-semibold hover:bg-[#5f0384] transition-colors disabled:opacity-50"
            // disabled={Object.keys(errors).length > 0}
          >
            Save address
          </button>
        </div>
      </div>

      {/* Location Picker Modal */}
      <LocationPickerModal
        isOpen={isLocationPickerOpen}
        onClose={() => setIsLocationPickerOpen(false)}
        onLocationSelect={handleLocationSelect}
        initialLocation={
          formData.geoLocation.coordinates[0] !== 0
            ? {
                lat: formData.geoLocation.coordinates[1],
                lng: formData.geoLocation.coordinates[0],
              }
            : undefined
        }
        googleMapsApiKey={googleMapsApiKey}
      />
    </div>
  );
};

export default AddAddressModal;
